import React, { useEffect, useRef, useState } from 'react'
import { Prompt, useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import MenuItem from '@mui/material/MenuItem'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import FormControl from '@mui/material/FormControl'
import Typography from '@mui/material/Typography'
import Add from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import BottomButtonGroup, { FORM_IMAGE_CLEAR_BUTTON_SX as IMAGE_CLEAR_BUTTON_SX, FORM_WIDE_UPLOAD_BUTTON_SX } from '../../component/BottomButtonGroup'
import { FORM_IMAGE_PREVIEW_FRAME_SX, FORM_PANEL_SX } from '../../component/formSurfaceStyles'
import { createListLinkTarget, getLocationState } from '../../utils/listPageState'
import {
    APP_COUNTRY_IMAGE_FILE_ACCEPT,
    APP_COUNTRY_IMAGE_FILE_ERROR_MESSAGE,
    applyAppCountryDraft,
    clearAppCountryDraft,
    createAppCountryDraftPayload,
    isAppCountryImageFile,
    NEW_APP_COUNTRY_DRAFT_KEY,
    readAppCountryDraft,
    saveAppCountryDraft,
} from './appCountryDraftUtils'
import {
    APP_COUNTRY_IMAGE_LEAVE_MESSAGE,
    hasPendingAppCountryImage,
    usePendingAppCountryImageUnloadGuard,
} from './appCountryLeaveGuard'

const IMAGE_PANEL_SX = FORM_PANEL_SX

const IMAGE_PANEL_HEADER_SX = {
    mb: 1.5,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 1.5,
}

const IMAGE_PANEL_BODY_SX = {
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5,
}

const IMAGE_PREVIEW_FRAME_SX = {
    ...FORM_IMAGE_PREVIEW_FRAME_SX,
    width: '100%',
    height: { xs: 220, md: 280 },
}

const IMAGE_PREVIEW_SX = {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    display: 'block',
}

const getImageFileName = (imageFile) => {
    if (!imageFile || typeof imageFile !== 'object') {
        return ''
    }

    return imageFile.name || ''
}

// 국가 이미지를 미리보기, 업로드, 선택 취소까지 한 패널 안에서 관리한다.
function CountryImageUploadPanel({
    imageSrc,
    imageFile,
    disabled,
    onUpload,
    hasPendingFile = false,
    onClear,
}) {
    const imageFileName = getImageFileName(imageFile)
    const fileInputRef = useRef(null)

    const handleClearClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }

        onClear?.()
    }

    return (
        <Paper variant="outlined" sx={IMAGE_PANEL_SX}>
            <Box sx={IMAGE_PANEL_HEADER_SX}>
                <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        국가 이미지
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        App 국가 화면에 노출되는 대표 이미지를 등록합니다.
                    </Typography>
                </Box>
                {hasPendingFile && (
                    <Button
                        size="small"
                        color="error"
                        variant="text"
                        startIcon={<RemoveIcon />}
                        onClick={handleClearClick}
                        sx={IMAGE_CLEAR_BUTTON_SX}
                        disabled={disabled}
                    >
                        선택 취소
                    </Button>
                )}
            </Box>
            <Box sx={IMAGE_PANEL_BODY_SX}>
                <Box sx={IMAGE_PREVIEW_FRAME_SX}>
                    <Box
                        component="img"
                        src={imageSrc || '/images/noimage.jpg'}
                        alt="국가 이미지 미리보기"
                        sx={IMAGE_PREVIEW_SX}
                    />
                </Box>
                <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        이미지 업로드
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
                        JPG, PNG, GIF, WEBP 형식의 이미지를 업로드할 수 있습니다.
                    </Typography>
                    <FormControl fullWidth>
                        <TextField
                            inputProps={{ accept: APP_COUNTRY_IMAGE_FILE_ACCEPT }}
                            inputRef={fileInputRef}
                            style={{ display: 'none' }}
                            name="image"
                            id="image"
                            type="file"
                            disabled={disabled}
                            onChange={onUpload}
                        />
                        <Box component="label" htmlFor="image" sx={{ display: 'block' }}>
                            <Button
                                fullWidth
                                variant="contained"
                                color="success"
                                component="span"
                                startIcon={<Add />}
                                sx={FORM_WIDE_UPLOAD_BUTTON_SX}
                                disabled={disabled}
                            >
                                이미지 선택
                            </Button>
                        </Box>
                    </FormControl>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        {imageFileName ? `선택한 파일: ${imageFileName}` : '저장 전 미리보기로 이미지가 올바른지 확인하세요.'}
                    </Typography>
                </Box>
            </Box>
        </Paper>
    )
}

export default function NewAppCountry() {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const curPage = locationState.curPage || 1
    const searchText = locationState.searchText || ''
    const searchSelect = locationState.searchSelect || { searchType: '' }
    const listSearch = location.search || ''
    const [saving, setSaving] = useState(false)
    const [params, setParams] = useState({
        image: '',
        continents: '',
        cCode: '',
        lCode: '',
        nameKr: '',
        nameEn: '',
        nameLo: '',
    })
    const [mainImage, setMainImage] = useState('')
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [draftReady, setDraftReady] = useState(false)
    const [navigationUnlocked, setNavigationUnlocked] = useState(false)
    const initialDraftSnapshotRef = useRef('')
    const leaveGuardEnabled = hasPendingAppCountryImage(params) && !saving && !navigationUnlocked

    usePendingAppCountryImageUnloadGuard(leaveGuardEnabled)

    useEffect(() => {
        const baseParams = {
            image: '',
            continents: '',
            cCode: '',
            lCode: '',
            nameKr: '',
            nameEn: '',
            nameLo: '',
        }
        const storedDraft = readAppCountryDraft(NEW_APP_COUNTRY_DRAFT_KEY)
        const restoredDraft = applyAppCountryDraft({
            baseParams,
            draft: storedDraft,
        })

        initialDraftSnapshotRef.current = JSON.stringify(createAppCountryDraftPayload({
            params: baseParams,
        }))

        setParams((prevParams) => ({
            ...prevParams,
            ...restoredDraft.params,
        }))
        setDraftReady(true)
    }, [])

    useEffect(() => {
        if (!draftReady) {
            return
        }

        // App 국가 등록은 목록 query를 건드리지 않고 폼 draft만 sessionStorage에 보존한다.
        const nextDraft = createAppCountryDraftPayload({
            params,
        })
        const nextDraftSnapshot = JSON.stringify(nextDraft)

        if (nextDraftSnapshot === initialDraftSnapshotRef.current) {
            clearAppCountryDraft(NEW_APP_COUNTRY_DRAFT_KEY)
            return
        }

        saveAppCountryDraft(NEW_APP_COUNTRY_DRAFT_KEY, nextDraft)
    }, [draftReady, params])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const moveToList = (snackbarState) => {
        setNavigationUnlocked(true)
        clearAppCountryDraft(NEW_APP_COUNTRY_DRAFT_KEY)

        // 저장 후 이동은 이탈 경고를 잠시 풀고, 목록 조건은 그대로 유지한다.
        setTimeout(() => {
            history.push(createListLinkTarget({
                pathname: '/countrys',
                search: listSearch,
                ...(snackbarState ? {
                    openSnackbar: true,
                    severitySnackbar: snackbarState.severity,
                    messageSnackbar: snackbarState.message,
                } : {}),
                curPage: curPage,
                searchText: searchText,
                searchSelect: searchSelect
            }))
        }, 0)
    }

    const handleBackClick = (event) => {
        if (!leaveGuardEnabled) {
            clearAppCountryDraft(NEW_APP_COUNTRY_DRAFT_KEY)
            return
        }

        event.preventDefault()

        if (!window.confirm(APP_COUNTRY_IMAGE_LEAVE_MESSAGE)) {
            return
        }

        moveToList()
    }

    const handleUpdateField = (e) => {
        setParams((prevParams) => ({
            ...prevParams,
            [e.target.name]: e.target.value
        }))
    }

    const handleUploadClick = (e) => {
        e.preventDefault()
        const { name, files } = e.target
        const file = files[0]

        if (!file) {
            return
        }

        if (!isAppCountryImageFile(file)) {
            e.target.value = ''
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(APP_COUNTRY_IMAGE_FILE_ERROR_MESSAGE)
            return
        }

        let reader = new FileReader()

        reader.onloadend = () => {
            setParams(prevParams => ({
                ...prevParams,
                [name]: file
            }))

            setMainImage(reader.result)
        }
    
        reader.readAsDataURL(file)
    }

    const handleClearSelectedImage = () => {
        setParams(prevParams => ({
            ...prevParams,
            image: '',
        }))
        setMainImage('')
    }

    const createData = async (params) => {
        try {
            setSaving(true)

            const { data } = await API({
                method: 'post',
                url: '/api/v1/admin/appCountry/create',
                data: params,
                headers: { "Content-Type": "multipart/form-data" }
            })

            if (data.code === 200) {
                // 등록 후에도 이전 App 국가 목록 조건으로 그대로 복귀시킨다.
                moveToList({
                    severity: 'success',
                    message: data.message,
                })
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('App 국가를 등록하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        if (saving) {
            return
        }

        const continents = params.continents
        const cCode = params.cCode.trim().toLowerCase()
        const lCode = params.lCode.trim().toLowerCase()
        const nameKr = params.nameKr.trim()
        const nameEn = params.nameEn.trim()
        const nameLo = params.nameLo.trim()

        if (!params.image) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('이미지를 업로드해주세요.')
            return
        }

        if (!continents || !cCode || !lCode || !nameKr || !nameEn) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('필수 입력값을 확인해주세요.')
            return
        }

        const formData = new FormData()
        formData.append('image',params.image)
        formData.append('continents', continents)
        formData.append('cCode', cCode)
        formData.append('lCode', lCode)
        formData.append('nameKr', nameKr)
        formData.append('nameEn', nameEn)
        formData.append('nameLo', nameLo)

        createData(formData)
    }

    return (
        <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Title>App 국가 등록</Title>
                <Prompt when={leaveGuardEnabled} message={APP_COUNTRY_IMAGE_LEAVE_MESSAGE} />
                <Box component="form" encType="multipart/form-data" onSubmit={handleSubmit} sx={{ opacity: saving ? 0.6 : 1 }}>
                    <Grid container spacing={2} sx={{ marginTop: 2 }}>
                        <Grid item xs={12}>
                            <CountryImageUploadPanel
                                imageSrc={mainImage}
                                imageFile={params.image}
                                disabled={saving}
                                onUpload={handleUploadClick}
                                hasPendingFile={params.image instanceof File}
                                onClear={handleClearSelectedImage}
                            />
                        </Grid>
                        <Grid item xs={4} md={4} sm={4}>
                            <FormControl fullWidth>
                                <TextField
                                    required
                                    label="대륙"
                                    select
                                    id="continents"
                                    name="continents"
                                    value={params.continents}
                                    size="small"
                                    sx={{ minWidth: 150 }}
                                    disabled={saving}
                                    onChange={handleUpdateField}
                                >
                                    <MenuItem value={'asia'}>아시아</MenuItem>
                                    <MenuItem value={'oceania'}>오세아니아</MenuItem>
                                    <MenuItem value={'n-america'}>북아메리카</MenuItem>
                                    <MenuItem value={'s-america'}>남아메리카</MenuItem>
                                    <MenuItem value={'europe'}>유럽</MenuItem>
                                    <MenuItem value={'africa'}>아프리카</MenuItem>
                                </TextField>
                            </FormControl>
                        </Grid>
                        <Grid item xs={4} md={4} sm={4}>
                            <FormControl fullWidth>
                                <TextField
                                    required
                                    label="국가코드"
                                    id="cCode"
                                    name="cCode"
                                    value={params.cCode || ''}
                                    disabled={saving}
                                    onChange={handleUpdateField}
                                    size="small"
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={4} md={4} sm={4}>
                            <FormControl fullWidth>
                                <TextField
                                    required
                                    label="언어코드"
                                    id="lCode"
                                    name="lCode"
                                    value={params.lCode || ''}
                                    disabled={saving}
                                    onChange={handleUpdateField}
                                    size="small"
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={12} md={12}>
                            <FormControl fullWidth>
                                <TextField
                                    required
                                    label="국가명(한국어)"
                                    multiline
                                    rows={3}
                                    id="nameKr"
                                    name="nameKr"
                                    value={params.nameKr || ''}
                                    disabled={saving}
                                    onChange={handleUpdateField}
                                    size="small"
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={12} md={12}>
                            <FormControl fullWidth>
                                <TextField
                                    required
                                    label="국가명(영어)"
                                    multiline
                                    rows={3}
                                    id="nameEn"
                                    name="nameEn"
                                    value={params.nameEn || ''}
                                    disabled={saving}
                                    onChange={handleUpdateField}
                                    size="small"
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={12} md={12}>
                            <FormControl fullWidth>
                                <TextField
                                    label="국가명(현지어)"
                                    multiline
                                    rows={3}
                                    id="nameLo"
                                    name="nameLo"
                                    value={params.nameLo || ''}
                                    disabled={saving}
                                    onChange={handleUpdateField}
                                    size="small"
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                    <BottomButtonGroup
                        path="/countrys"
                        search={listSearch}
                        visible={true}
                        curPage={curPage}
                        searchText={searchText}
                        searchSelect={searchSelect}
                        submitDisabled={saving}
                        submitLabel={saving ? '저장 중...' : '저장'}
                        onBackClick={handleBackClick}
                    />
                </Box>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Paper>
        </Grid>
    )
}
